import { Box, Collapse, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';

import PaymentInvoiceAccordionContent from '../../../components/tableComponent/PaymentInvoiceAccordionContent';
import { colors } from '../../../components/theme/theme';
import CommonStatusChip from '../../../components/ui-components/StatusChipComponent';
import { PaymentInvoiceDetail } from '../../../services/payment-services';

interface PaymentHistoryMobileCardProps {
    row: Record<string, unknown>;
}

/**
 * Mobile/tablet-only card rendering of a single payment-history row (collapsed summary +
 * expandable itemized invoice breakdown). Desktop keeps using CommonTable in paymentHistory.tsx.
 */
export const PaymentHistoryMobileCard: React.FC<PaymentHistoryMobileCardProps> = ({ row }) => {
    const [expanded, setExpanded] = useState(false);
    const invoices = (row.invoices as PaymentInvoiceDetail[]) || [];
    const invoiceCount = Number(row.invoiceCount) || invoices.length;

    return (
        <Box
            sx={{
                border: `1px solid ${expanded ? colors.primaryLight[100] : colors.greyPreset[30]}`,
                borderRadius: 1.5,
                backgroundColor: colors.fullWhite,
                overflow: 'hidden',
            }}
        >
            <Box sx={{ px: 1.5, py: 1.25, cursor: 'pointer' }} onClick={() => setExpanded(prev => !prev)}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                    <Typography variant="subtitle1" fontWeight={700}>
                        Contract #{String(row.contractId || 'N/A')}
                    </Typography>
                    <Stack direction="row" alignItems="center" gap={0.5} flexShrink={0}>
                        <Typography variant="subtitle1" fontWeight={700}>
                            {String(row.amount || '')}
                        </Typography>
                        <span
                            className="icon-arrow-drop-down"
                            style={{
                                fontSize: 20,
                                color: colors.greyPreset[100],
                                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease',
                                display: 'inline-block',
                            }}
                        />
                    </Stack>
                </Stack>
                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" mt={0.5}>
                    <Typography variant="body2" color="textSecondary">
                        {String(row.paymentDate || 'N/A')}
                    </Typography>
                    {invoiceCount > 1 && (
                        <CommonStatusChip
                            label={`${invoiceCount} Invoices`}
                            chipBgColor={colors.successPreset[30]}
                            sx={{ fontSize: 11, padding: '2px 6px' }}
                        />
                    )}
                </Stack>
                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" mt={0.75}>
                    <CommonStatusChip
                        label={String(row.paymentType || 'N/A')}
                        chipBgColor={colors.greyPreset[90]}
                        chipTextColor={colors.fullBlack}
                        sx={{ fontSize: 11, padding: '2px 6px' }}
                    />
                    <Typography variant="caption" color="textSecondary">
                        Ref: {String(row.referenceNumber || 'N/A')}
                    </Typography>
                </Stack>
            </Box>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <PaymentInvoiceAccordionContent invoices={invoices} withTitleTooltips />
            </Collapse>
        </Box>
    );
};
